import { ReactNode, useState } from 'react'
import {
  WppInput,
  WppActionButton,
  WppButton,
  WppInlineMessage,
  WppListItem,
  WppModal,
  WppSelect,
  WppTypography,
} from '@wppopen/components-library-react'
import FileUploader from 'components/FileUploader/FileUploader'
import { CircleFlag } from 'react-circle-flags'
import { MANUAL_UPLOAD_FILE_LIMIT_SIZE } from 'utils/constants'
import clsx from 'clsx'
import type { FileItemType, FileUploadEventDetail, SelectChangeEventDetail } from '@wppopen/components-library'
import type { WppFileUploadCustomEvent, WppSelectCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { FEApiError } from 'utils/errorHandling'
import templateXlsURL from '../../../assets/pdp_generator_product_template.xlsx'
import templateCsvURL from '../../../assets/pdp_generator_product_template.csv'
import modalStyles from '../Modal.module.scss'
import styles from './UploadProjectData.module.scss'

interface uploadProjectDataProps {
  onSubmit: (file: FileItemType, importProfile: string) => Promise<void>
  onCancel: () => void
  modalTitle: string
  submitButtonText: string
  isApiLoading: boolean
  shouldModalOpen: boolean
  selectedRegion?: any
  children?: ReactNode
}

interface TemplateOptions {
  XLS: 'Commerce Studio XLS Template'
  CSV: 'Commerce Studio CSV Template'
}

interface AcceptConfig {
  [key: string]: string[]
}

const TEMPLATE_OPTIONS: TemplateOptions = {
  XLS: 'Commerce Studio XLS Template',
  CSV: 'Commerce Studio CSV Template',
}

const { XLS, CSV } = TEMPLATE_OPTIONS

export default function UploadProjectData({
  onSubmit,
  onCancel,
  modalTitle,
  submitButtonText,
  isApiLoading,
  shouldModalOpen,
  selectedRegion,
  children,
}: uploadProjectDataProps) {
  const transformTo2DigitSize = MANUAL_UPLOAD_FILE_LIMIT_SIZE / 1000000 //for example 50
  const [file, setFile] = useState<FileItemType[]>()
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [errorMessage, setErrorMessage] = useState<string>()
  const [selectedTemplate, setSelectedTemplate] = useState<string>(XLS)

  // handle the file change
  const onFileChange = (e: WppFileUploadCustomEvent<FileUploadEventDetail>) => {
    setErrorMessage(undefined)
    setValidationErrors([])
    const file = e.detail.value as FileItemType[]

    if (file?.length && file?.[0].size! > MANUAL_UPLOAD_FILE_LIMIT_SIZE) {
      setValidationErrors([`File size should be less than ${transformTo2DigitSize}MB`])
      return
    }

    setFile(file)
  }

  const handleInternalSubmit = () => {
    if (!file?.length) {
      setErrorMessage('Please upload a file')
      setValidationErrors(['Please upload a file'])
      return
    }

    // check file type matches the selected template
    const fileType = file[0]?.type
    const expectedFileType =
      selectedTemplate === TEMPLATE_OPTIONS.CSV
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

    const isValidFileType =
      fileType === expectedFileType ||
      (selectedTemplate === TEMPLATE_OPTIONS.XLS && fileType === 'application/vnd.ms-excel')

    if (!isValidFileType) {
      setErrorMessage('Invalid file type.')
      return
    }

    const importProfile = selectedTemplate === TEMPLATE_OPTIONS.CSV ? 'csv' : 'excel'

    // reset the error message and validation errors
    setErrorMessage(undefined)
    setValidationErrors([])

    onSubmit(file[0], importProfile).catch((err: FEApiError) => {
      const { errors, message } = err

      if (errors) {
        const errorMessages = errors.map(({ message }) => message)
        setValidationErrors(errorMessages)
      } else if (message) {
        setErrorMessage(message)
      }
    })
  }

  const getAcceptConfig = (): AcceptConfig => {
    if (selectedTemplate === CSV) {
      return {
        'text/csv': ['.csv'],
      }
    }
    return {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    }
  }

  const handleTemplateChange = async (e: WppSelectCustomEvent<SelectChangeEventDetail>) => {
    setSelectedTemplate(e.detail.value)
    // Reset file and errors when template type changes
    setErrorMessage(undefined)
    setValidationErrors([])
  }

  const getCurrentTemplateURL = () => {
    return selectedTemplate === CSV ? templateCsvURL : templateXlsURL
  }

  const getCurrentTemplateFilename = () => {
    return selectedTemplate === CSV ? 'pdp_generator_product_template.csv' : 'pdp_generator_product_template.xlsx'
  }

  return (
    <WppModal
      open={shouldModalOpen}
      className={clsx(modalStyles.modal, styles.modalReposition)}
      formConfig={{
        onSubmit: e => {
          e.preventDefault()
          handleInternalSubmit()
        },
        onReset: () => {
          onCancel()
        },
      }}
      data-testid="upload-products-modal"
    >
      <WppTypography tag="h3" type="xl-heading" slot="header">
        {modalTitle}
      </WppTypography>
      <div slot="body">
        {children}
        {selectedRegion && (
          <WppInput
            data-testid="region-selected"
            name="region_id"
            labelConfig={{ text: 'Region' }}
            required
            readOnly
            value={`${selectedRegion.region_name} (${selectedRegion.region_locale?.toUpperCase().replace('_', '-')})`}
          >
            <CircleFlag countryCode={selectedRegion.region_flag || 'none'} height={20} slot="icon-start" />
          </WppInput>
        )}

        <WppSelect
          data-testid="import-profile"
          name="import_profile"
          labelConfig={{ text: 'Import profile' }}
          required
          withSearch={false}
          value={selectedTemplate}
          onWppChange={handleTemplateChange}
        >
          <WppListItem value={XLS} data-testid="import-option-xls">
            <p slot="label">{XLS}</p>
          </WppListItem>
          <WppListItem value={CSV} data-testid="import-option-csv">
            <p slot="label">{CSV}</p>
          </WppListItem>
        </WppSelect>

        <FileUploader
          name="upload-file"
          acceptConfig={getAcceptConfig()}
          file={file?.length ? file[0] : undefined}
          onFileChange={onFileChange}
          size={transformTo2DigitSize}
          maxFiles={1}
          messageType={!!validationErrors.length || !!errorMessage ? 'error' : undefined}
          fileValidation={validationErrors}
          data-testid="upload-file"
        />

        <WppActionButton
          onClick={() => {
            // create a link and click it to download the template
            const link = document.createElement('a')
            link.href = getCurrentTemplateURL()
            link.download = getCurrentTemplateFilename()
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }}
          data-testid="download-template-button"
        >
          <u>Download Commerce Studio {selectedTemplate === CSV ? 'CSV' : 'XLS'} template</u>
        </WppActionButton>
      </div>

      <div slot="actions">
        <div className={modalStyles.modal__actionsGroup}>
          <WppButton type="reset" variant="secondary" data-testid="modal-reset-button" disabled={isApiLoading}>
            Cancel
          </WppButton>
          <WppButton type="submit" variant="primary" data-testid="modal-submit-button" loading={isApiLoading}>
            {submitButtonText}
          </WppButton>
        </div>
        {errorMessage && <WppInlineMessage size="s" message={errorMessage} type="error" data-testid="inline-error" />}
      </div>
    </WppModal>
  )
}
